import React, { useState } from 'react';

// ── ONE quotation editor for the whole app ────────────────────────────────────
// CRM (a lead's quotation) and Finance → Quotations (a client's quotation) both build
// their "New / Edit Quotation" window from these pieces, so the header, step headings,
// items table, totals and footer look and behave identically everywhere.
//
// Editor line shape (each page converts to/from its own stored shape):
//   { service_id, particular, description, sac_code, amount, discount, charge_type, gst_applies }
//   amount   = the line figure (qty × rate as one number)    discount = ₹ off this line
//   charge_type 'professional' (default, GST applies) | 'government' (paid on the client's
//   behalf — no GST unless gst_applies is ticked). See utils/charges.js.

export const GST_PCT = 18;
const truthy = (v) => v === true || v === 'true';

export const blankQuoteItem = () => ({ service_id: null, particular: '', description: '', sac_code: '', amount: '', discount: '', charge_type: 'professional', gst_applies: true });

export const quoteLineNet = (it) => Number(it.amount || 0) - Number(it.discount || 0);
export const quoteLineIsGovt = (it) => it.charge_type === 'government';
export const quoteLinePassThrough = (it) => quoteLineIsGovt(it) && !truthy(it.gst_applies);

export function computeQuoteTotals(items, gstRegistered = true) {
  const list = items || [];
  const taxable = list.filter(it => !quoteLinePassThrough(it)).reduce((s, it) => s + quoteLineNet(it), 0);
  const passThrough = list.filter(quoteLinePassThrough).reduce((s, it) => s + quoteLineNet(it), 0);
  const gst = gstRegistered ? taxable * GST_PCT / 100 : 0;
  return { taxable, passThrough, gst, total: taxable + gst + passThrough, hasGovt: list.some(quoteLineIsGovt) };
}

export const money = (v) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const quoteInputStyle = (T) => ({ width: '100%', border: `1px solid ${T.inputBorder}`, borderRadius: 10, padding: '9px 12px', fontSize: 13, outline: 'none', background: T.inputBg, color: T.text, boxSizing: 'border-box' });
export const QuoteLabel = ({ T, children, req }) => (
  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6 }}>{children}{req && <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>}</label>
);

// Numbered step heading with a hairline — "1  QUOTATION DETAILS ────"
export function QuoteSection({ n, title, right, T, children, last }) {
  return (
    <div style={{ marginBottom: last ? 0 : 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{n}</div>
        <h4 style={{ fontSize: 12, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>{title}</h4>
        <div style={{ flex: 1, height: 1, background: T.border }} />
        {right}
      </div>
      {children}
    </div>
  );
}

// The window: navy header (title + quote no.), scrolling body, footer with the grand total and actions.
export function QuotationShell({ title, quoteNo, onClose, children, totalLabel = 'Total Amount:', total, onSave, saving, saveLabel, T, darkMode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 80, padding: 16 }}>
      <div style={{ background: T.bg2, borderRadius: 20, width: '100%', maxWidth: 1100, height: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 32px', background: '#0f1f45', borderRadius: '20px 20px 0 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
                <rect x="3" y="4" width="18" height="18" rx="3" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                <circle cx="12" cy="15" r="1" fill="white" /><line x1="9" y1="15" x2="9.01" y2="15" strokeWidth="2.5" /><line x1="15" y1="15" x2="15.01" y2="15" strokeWidth="2.5" />
              </svg>
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0 }}>{title}</h3>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', margin: '3px 0 0' }}>Quote No.: <strong style={{ color: '#fff' }}>{quoteNo || 'Auto-generated'}</strong></p>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ width: 36, height: 36, borderRadius: 12, border: 'none', background: 'rgba(255,255,255,0.1)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 28, background: T.bg3 }}>{children}</div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 32px', borderTop: `1px solid ${T.border}`, background: T.bg2, flexShrink: 0, borderRadius: '0 0 20px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
            <span style={{ fontSize: 13, color: T.text3 }}>{totalLabel}</span>
            <span style={{ fontSize: 17, fontWeight: 800, color: T.text }}>₹{money(total)}</span>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 20px', borderRadius: 10, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
            <button type="button" onClick={onSave} disabled={saving} style={{ padding: '10px 28px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1, boxShadow: '0 4px 12px rgba(15,31,69,0.3)' }}>
              {saving ? 'Saving...' : saveLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const COLS = '2.7fr 1.05fr 0.85fr 0.8fr 0.8fr 0.9fr 30px';

// Step "Service Items": the lines table + the totals block + a prominent "add another" button.
//   services      Services Master rows (professional lines must be picked from here)
//   withScope     show the editable scope-of-work text under each professional line (CRM)
//   gstRegistered GST @18% shows on the totals only for a GST-registered firm
export function QuotationItemsSection({ n = 2, items, onChange, services = [], withScope = false, gstRegistered = true, T, darkMode }) {
  const [pickerFor, setPickerFor] = useState(null);
  const [search, setSearch] = useState('');
  const patch = (i, p) => onChange(items.map((it, k) => (k === i ? { ...it, ...p } : it)));
  const remove = (i) => onChange(items.filter((_, k) => k !== i));
  const add = () => onChange([...items, blankQuoteItem()]);
  const totals = computeQuoteTotals(items, gstRegistered);
  const filtered = services.filter(sv => !search || (sv.name || '').toLowerCase().includes(search.toLowerCase()) || (sv.service_code || '').toLowerCase().includes(search.toLowerCase()));
  const cell = { padding: '0 8px' };
  const bare = { width: '100%', background: 'transparent', border: 'none', outline: 'none', fontSize: 13, color: T.text, boxSizing: 'border-box' };

  const pickService = (i, sv) => {
    patch(i, {
      service_id: sv.id, particular: sv.name,
      description: withScope ? (sv.invoice_description || sv.description || '') : '',
      sac_code: sv.sac_code || sv.service_code || '',
      amount: sv.default_amount || sv.base_price || sv.rate || '',
    });
    setPickerFor(null); setSearch('');
  };
  const sumRow = (label, val, strong, color) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 40, padding: strong ? '8px 0 0' : '2px 0', marginTop: strong ? 6 : 0, borderTop: strong ? `1px solid ${T.border}` : 'none', fontSize: strong ? 15 : 13, fontWeight: strong ? 800 : 500, color: color || (strong ? T.text : T.text2) }}>
      <span>{label}</span><span>₹{money(val)}</span>
    </div>
  );

  return (
    <QuoteSection n={n} title="Service Items" T={T} last
      right={
        <button type="button" onClick={add} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: `1px solid ${darkMode ? 'rgba(59,130,246,0.3)' : '#bfdbfe'}`, background: darkMode ? 'rgba(59,130,246,0.1)' : '#eff6ff', color: '#3b82f6' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          Add Row
        </button>
      }>
      <div style={{ border: `1px solid ${T.border}`, borderRadius: 16, background: T.bg2, boxShadow: darkMode ? '0 2px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(15,31,69,0.06)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: COLS, padding: '11px 16px', background: '#0f1f45', borderRadius: '16px 16px 0 0', color: '#fff', fontSize: 11.5, fontWeight: 600 }}>
          <div style={cell}>Service / Description</div>
          <div style={cell}>Type</div>
          <div style={{ ...cell, textAlign: 'center' }}>SAC Code</div>
          <div style={{ ...cell, textAlign: 'right' }}>Amount (₹)</div>
          <div style={{ ...cell, textAlign: 'right' }}>Discount (₹)</div>
          <div style={{ ...cell, textAlign: 'right' }}>Net Amount (₹)</div>
          <div />
        </div>

        {items.map((it, i) => {
          const govt = quoteLineIsGovt(it);
          const gstOn = truthy(it.gst_applies);
          return (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: COLS, padding: '10px 16px', alignItems: 'start', borderBottom: `1px solid ${T.border}` }}>
              <div style={{ ...cell, position: 'relative' }}>
                {govt ? (
                  <input type="text" value={it.particular} onChange={e => patch(i, { particular: e.target.value, service_id: null })}
                    placeholder="Government fee, e.g. ITR late fee (Sec 234F)" style={{ ...bare, padding: '7px 0' }} />
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="text" placeholder="Search & select service..."
                        value={pickerFor === i ? search : it.particular}
                        onFocus={() => { setSearch(''); setPickerFor(i); }}
                        onChange={e => { setSearch(e.target.value); setPickerFor(i); }}
                        onBlur={() => setPickerFor(null)}
                        style={{ ...bare, padding: '7px 0', fontWeight: 600 }} />
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{ flexShrink: 0 }}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                    </div>
                    {pickerFor === i && (
                      <div onMouseDown={e => e.preventDefault()} style={{ position: 'absolute', left: 0, top: 'calc(100% + 2px)', background: T.bg2, border: `1px solid ${T.border2}`, borderRadius: 14, boxShadow: darkMode ? '0 16px 48px rgba(0,0,0,0.6)' : '0 16px 48px rgba(15,31,69,0.18)', zIndex: 9999, width: 400 }}>
                        <div style={{ maxHeight: 230, overflowY: 'auto', scrollbarWidth: 'none', padding: '6px 0' }}>
                          {filtered.length === 0 ? (
                            <p style={{ textAlign: 'center', fontSize: 12, color: T.text3, padding: '16px 0', margin: 0 }}>No matching service in Service Master</p>
                          ) : filtered.map(sv => (
                            <button key={sv.id} type="button" onClick={() => pickService(i, sv)}
                              style={{ width: '100%', textAlign: 'left', padding: '9px 16px', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}
                              onMouseEnter={e => { e.currentTarget.style.background = T.bg3; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                              <div style={{ minWidth: 0 }}>
                                <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sv.name}</p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                                  {sv.service_code && <span style={{ fontFamily: 'monospace', fontSize: 11, background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9', color: T.text3, padding: '1px 6px', borderRadius: 4 }}>{sv.service_code}</span>}
                                  {sv.category && <span style={{ fontSize: 11, color: T.text3 }}>{sv.category}</span>}
                                </div>
                              </div>
                              {(sv.default_amount || sv.base_price || sv.rate) && <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', flexShrink: 0, marginTop: 2 }}>₹{Number(sv.default_amount || sv.base_price || sv.rate).toLocaleString('en-IN')}</span>}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {withScope && it.particular && (
                      <textarea rows="2" value={it.description} onChange={e => patch(i, { description: e.target.value })} placeholder="Scope of work (optional)"
                        style={{ width: '100%', marginTop: 4, fontSize: 12, color: T.text2, border: `1px solid ${T.border}`, borderRadius: 8, padding: '6px 8px', outline: 'none', resize: 'vertical', background: T.bg3, boxSizing: 'border-box', fontFamily: 'inherit' }} />
                    )}
                  </>
                )}
                {govt && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, fontSize: 11.5, color: T.text3, cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={gstOn} onChange={e => patch(i, { gst_applies: e.target.checked })} style={{ accentColor: '#2563eb', cursor: 'pointer' }} />
                    Charge GST on this line {!gstOn && <span style={{ color: T.text3 }}>· paid on the client's behalf, no GST</span>}
                  </label>
                )}
              </div>

              <div style={cell}>
                <select value={govt ? 'government' : 'professional'}
                  onChange={e => { const v = e.target.value; patch(i, { charge_type: v, gst_applies: v !== 'government', ...(v === 'government' ? { service_id: null } : {}) }); }}
                  style={{ width: '100%', fontSize: 12, border: `1px solid ${T.border2}`, borderRadius: 8, padding: '6px 6px', background: govt ? (darkMode ? 'rgba(245,158,11,0.12)' : '#fffbeb') : T.inputBg, color: govt ? '#b45309' : T.text2, outline: 'none' }}>
                  <option value="professional">Professional</option>
                  <option value="government">Government</option>
                </select>
              </div>

              <div style={cell}>
                <input type="text" placeholder="SAC" value={it.sac_code || ''} onChange={e => patch(i, { sac_code: e.target.value })}
                  style={{ ...bare, textAlign: 'center', color: T.text2, fontFamily: 'monospace', padding: '7px 0' }} />
              </div>
              <div style={cell}>
                <input type="number" min="0" placeholder="0.00" value={it.amount} onChange={e => patch(i, { amount: e.target.value })}
                  style={{ ...bare, textAlign: 'right', padding: '7px 0' }} />
              </div>
              <div style={cell}>
                <input type="number" min="0" placeholder="0.00" value={it.discount} onChange={e => patch(i, { discount: e.target.value })}
                  style={{ ...bare, textAlign: 'right', color: '#f97316', padding: '7px 0' }} />
              </div>
              <div style={{ ...cell, textAlign: 'right', fontSize: 13, fontWeight: 700, color: T.text, padding: '7px 8px' }}>₹{money(quoteLineNet(it))}</div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {items.length > 1 && (
                  <button type="button" onClick={() => remove(i)} title="Remove line"
                    style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: 'none', color: T.text3, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onMouseEnter={e => { e.currentTarget.style.background = darkMode ? 'rgba(239,68,68,0.12)' : '#fef2f2'; e.currentTarget.style.color = '#ef4444'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = T.text3; }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}

        <div style={{ padding: '12px 16px', borderTop: `2px solid ${T.border}`, background: T.bg3, borderRadius: '0 0 16px 16px', display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ minWidth: 300 }}>
            {(totals.hasGovt || (gstRegistered && totals.gst > 0)) && sumRow(totals.hasGovt ? 'Professional fees' : 'Subtotal', totals.taxable)}
            {gstRegistered && sumRow(`GST @ ${GST_PCT}%`, totals.gst)}
            {totals.hasGovt && sumRow('Government fees (no GST)', totals.passThrough)}
            {sumRow('Grand Total', totals.total, true)}
          </div>
        </div>
      </div>

      <button type="button" onClick={add}
        style={{ width: '100%', marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '12px 0', borderRadius: 12, cursor: 'pointer', border: `2px dashed ${darkMode ? 'rgba(59,130,246,0.4)' : '#93c5fd'}`, background: darkMode ? 'rgba(59,130,246,0.06)' : 'rgba(239,246,255,0.7)', color: '#2563eb', fontSize: 13.5, fontWeight: 600 }}
        onMouseEnter={e => { e.currentTarget.style.background = darkMode ? 'rgba(59,130,246,0.12)' : '#dbeafe'; }}
        onMouseLeave={e => { e.currentTarget.style.background = darkMode ? 'rgba(59,130,246,0.06)' : 'rgba(239,246,255,0.7)'; }}>
        <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, lineHeight: 1 }}>+</span>
        Add another service or fee
        <span style={{ fontSize: 12, fontWeight: 400, color: '#60a5fa' }}>(professional or government fee)</span>
      </button>
    </QuoteSection>
  );
}
